import { useFeatureFlags } from 'front-end/src/app/hooks/useFeatureFlags'
import React from 'react'
import { ButtonPrimary, ButtonOutline } from 'front-end/src/components'
import { useModal } from 'front-end/src/app/hooks/useModal'
import { t, Trans } from '@lingui/macro'
import { notification } from 'front-end/src/components/common/notifications/Notifications'
import { useGoogleAuth } from 'front-end/src/app/hooks/useGoogleAuth'
import { useFormikContext } from 'formik'
import { useManageEventContext } from '../../../ManageEventContext'
import { PublishGoogleCalendarSyncModal } from './PublishGoogleCalendarSyncModal'
import { DiscardChangesModal } from './DiscardChangesModal'
import { linkBuilder } from 'shared/linkBuilder'
import { navigateTo } from 'front-end/src/app/util/appHistory'
import { ManageEventDetailDayjs } from 'front-end/src/pages/manage-events/event-edit/EventEditPage'
import { PublishConfirmModal } from './PublishConfirmModal'
import { useGetCalendarIntegrationsQuery } from 'front-end/src/pages/manage-events/event-edit/EventEdit.codegen'
import { useAffectedCampaignsAfterPatchLazyQuery } from 'front-end/src/pages/manage-events/event-edit/RecurringEventEdit.codegen'
import { prepareRecurringEventPayload } from 'front-end/src/pages/manage-events/event-edit/helpers'

type EventDetailActionsProps = {
  isDateRangeValid: boolean
}

export function EventDetailActions({
  isDateRangeValid
}: EventDetailActionsProps) {
  const { isRecurringSeries, event, updateEvent, updateRecurringEventSeries } =
    useManageEventContext()
  const { featureFlags } = useFeatureFlags()
  const { values, dirty, resetForm, isSubmitting, handleSubmit } =
    useFormikContext<ManageEventDetailDayjs>()
  const { data: orgCalData } = useGetCalendarIntegrationsQuery({
    fetchPolicy: 'cache-first',
    skip: !featureFlags?.calendars_integration || true
  })

  /*eslint-ignore*/
  const [getAffectedCampaigns] = useAffectedCampaignsAfterPatchLazyQuery({
    onCompleted: (data) => {
      publishConfirm(data.admin.recurringEvent.affectedCampaignsAfterPatch)
    }
  })

  const {
    open: openPublishGoogleCalendarSync,
    currentModal: publishGoogleCalendarSyncModal
  } = useModal()
  const { open: openDiscard, currentModal: discardModal } = useModal()
  const { open: openPublishConfirm, currentModal: publishConfirmModal } =
    useModal()

  const hasGoogleIntegration =
    !!orgCalData?.admin?.organisation?.calendars.google?.calendarId

  const skipGoogleAuthCheck =
    !featureFlags?.calendars_integration || !hasGoogleIntegration
  const { isAuthenticated } = useGoogleAuth(skipGoogleAuthCheck)

  const isNetworkRequest = isSubmitting
  const isDiscardDisabled = !dirty || isNetworkRequest
  const isSaveDisabled = isDiscardDisabled || !isDateRangeValid || !values.title
  const isPublishDisabled =
    (event.published_date && !dirty) ||
    isNetworkRequest ||
    !isDateRangeValid ||
    !values.title
  const exitUrl = isRecurringSeries
    ? linkBuilder.eventDetailSeries(event.id)
    : linkBuilder.eventDetail(event.id)

  const publish = async () => {
    if (isRecurringSeries) {
      await getAffectedCampaigns({
        variables: {
          eventId: event.id,
          data: prepareRecurringEventPayload(values)
        }
      })
    } else {
      publishConfirm()
    }
  }

  const publishConfirm = (relatedCampaigns = []) => {
    openPublishConfirm(PublishConfirmModal, { relatedCampaigns })
      .then(
        async ({ notifyInvitees, notifyRSVPdInvitees, emailIdentityId }) => {
          if (isRecurringSeries) {
            await updateRecurringEventSeries({
              payload: values,
              notifyInvitees,
              notifyRSVPdInvitees,
              emailIdentityId,
              publish: true
            })
          } else {
            await updateEvent({
              payload: values,
              notifyInvitees,
              notifyRSVPdInvitees,
              emailIdentityId,
              publish: true
            })
          }
        }
      )
      .catch(() =>
        notification.error(t`Publishing event has failed. Try again.`)
      )
  }

  return (
    <>
      {!event.published_date && (
        <ButtonPrimary
          disabled={isSaveDisabled}
          onClick={() => {
            handleSubmit()
          }}
          ml="4px"
        >
          <Trans>Save</Trans>
        </ButtonPrimary>
      )}
      <ButtonPrimary
        disabled={isPublishDisabled}
        onClick={async () => {
          if (
            featureFlags.calendars_integration &&
            hasGoogleIntegration &&
            !isAuthenticated
          ) {
            openPublishGoogleCalendarSync(PublishGoogleCalendarSyncModal).then(
              async () => {
                await publish()
              }
            )
          } else {
            await publish()
          }
        }}
        ml="4px"
        type="button"
      >
        <Trans>Publish</Trans>
      </ButtonPrimary>
      {event.published_date && (
        <ButtonOutline
          mr="4px"
          onClick={() => {
            if (isDiscardDisabled) {
              navigateTo(exitUrl)
            } else {
              openDiscard(DiscardChangesModal).then(async () => {
                resetForm()
                navigateTo(exitUrl)
              })
            }
          }}
          type="reset"
        >
          <Trans>Exit editing</Trans>
        </ButtonOutline>
      )}
      {publishGoogleCalendarSyncModal}
      {publishConfirmModal}
      {discardModal}
    </>
  )
}
